// frontend-dash/src/components/ColorThemePreview.js
// Live preview of all MD3 UI elements in the generated colors.
// Renders inside an isolated container with scoped CSS variables.

import React from 'react';

/**
 * Converts hex color like "#8F4C38" to "143 76 56" (RGB string for CSS vars).
 */
function hexToRgb(hex) {
  const c = hex.replace('#', '');
  return `${parseInt(c.substring(0, 2), 16)} ${parseInt(c.substring(2, 4), 16)} ${parseInt(c.substring(4, 6), 16)}`;
}

/**
 * Build a CSS variables style object from a scheme.
 */
function buildCssVarsStyle(scheme) {
  if (!scheme) return {};
  const map = {
    '--color-primary': scheme.primary,
    '--color-primary-on': scheme.onPrimary,
    '--color-primary-container': scheme.primaryContainer,
    '--color-primary-on-container': scheme.onPrimaryContainer,
    '--color-secondary': scheme.secondary,
    '--color-secondary-on': scheme.onSecondary,
    '--color-secondary-container': scheme.secondaryContainer,
    '--color-secondary-on-container': scheme.onSecondaryContainer,
    '--color-tertiary': scheme.tertiary,
    '--color-tertiary-on': scheme.onTertiary,
    '--color-tertiary-container': scheme.tertiaryContainer,
    '--color-tertiary-on-container': scheme.onTertiaryContainer,
    '--color-error': scheme.error,
    '--color-error-on': scheme.onError,
    '--color-error-container': scheme.errorContainer,
    '--color-error-on-container': scheme.onErrorContainer,
    '--color-surface': scheme.surface,
    '--color-surface-on': scheme.onSurface,
    '--color-surface-dim': scheme.surfaceDim,
    '--color-surface-bright': scheme.surfaceBright,
    '--color-surface-container-lowest': scheme.surfaceContainerLowest,
    '--color-surface-container-low': scheme.surfaceContainerLow,
    '--color-surface-container': scheme.surfaceContainer,
    '--color-surface-container-high': scheme.surfaceContainerHigh,
    '--color-surface-container-highest': scheme.surfaceContainerHighest,
    '--color-surface-variant': scheme.surfaceVariant,
    '--color-surface-on-variant': scheme.onSurfaceVariant,
    '--color-outline': scheme.outline,
    '--color-outline-variant': scheme.outlineVariant,
    '--color-inverse-surface': scheme.inverseSurface,
    '--color-inverse-on-surface': scheme.inverseOnSurface,
    '--color-inverse-primary': scheme.inversePrimary,
    '--color-scrim': scheme.scrim || '#000000',
  };

  const style = {};
  for (const [varName, hexValue] of Object.entries(map)) {
    if (hexValue) {
      style[varName] = hexToRgb(hexValue);
    }
  }
  return style;
}

/**
 * Small color swatch with label.
 */
const Swatch = ({ color, label, textColor }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className="w-10 h-10 rounded-lg shadow-elevation-1 border border-black/10"
      style={{ backgroundColor: color }}
      title={color}
    />
    <span className="text-[10px] font-medium opacity-70 text-center leading-tight" style={{ color: textColor }}>
      {label}
    </span>
  </div>
);

/**
 * Section of color swatches.
 */
const SwatchGroup = ({ title, swatches, textColor }) => (
  <div className="space-y-2">
    <h5 className="text-label-small font-bold uppercase tracking-wider opacity-60" style={{ color: textColor }}>{title}</h5>
    <div className="flex flex-wrap gap-2">
      {swatches.map((s, i) => (
        <Swatch key={i} color={s.color} label={s.label} textColor={textColor} />
      ))}
    </div>
  </div>
);

/**
 * Palette row showing tonal steps for one palette.
 */
const PaletteRow = ({ name, palette, textColor }) => {
  if (!palette) return null;
  const steps = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 100];
  return (
    <div className="space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60" style={{ color: textColor }}>{name}</span>
      <div className="flex gap-px rounded-lg overflow-hidden">
        {steps.map((step) => (
          <div
            key={step}
            className="flex-1 h-8 relative group cursor-pointer"
            style={{ backgroundColor: palette[step] }}
            title={`${name} ${step}: ${palette[step]}`}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[8px] font-bold" style={{ color: step > 50 ? '#000' : '#fff' }}>{step}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ColorThemePreview = ({ theme, previewMode = 'light' }) => {
  if (!theme) return null;

  const scheme = previewMode === 'dark' ? theme.dark : theme.light;
  if (!scheme) return null;

  const cssVarsStyle = buildCssVarsStyle(scheme);
  const textColor = scheme.onSurface;

  return (
    <div
      className="rounded-3xl overflow-hidden border border-outlineVariant/30"
      style={{
        ...cssVarsStyle,
        backgroundColor: `rgb(${cssVarsStyle['--color-surface']})`,
        color: `rgb(${cssVarsStyle['--color-surface-on']})`,
      }}
    >
      {/* Color Palette Strips */}
      {theme.palettes && (
        <div className="p-4 space-y-2" style={{ backgroundColor: `rgb(${cssVarsStyle['--color-surface-container-low'] || cssVarsStyle['--color-surface']})` }}>
          <PaletteRow name="Primary" palette={theme.palettes.primary} textColor={textColor} />
          <PaletteRow name="Secondary" palette={theme.palettes.secondary} textColor={textColor} />
          <PaletteRow name="Tertiary" palette={theme.palettes.tertiary} textColor={textColor} />
          <PaletteRow name="Neutral" palette={theme.palettes.neutral} textColor={textColor} />
          <PaletteRow name="Neutral Variant" palette={theme.palettes.neutralVariant} textColor={textColor} />
        </div>
      )}

      {/* Key Color Swatches */}
      <div className="p-4 space-y-4" style={{ backgroundColor: `rgb(${cssVarsStyle['--color-surface']})` }}>
        <SwatchGroup
          title="Primary"
          textColor={textColor}
          swatches={[
            { color: scheme.primary, label: 'Primary' },
            { color: scheme.onPrimary, label: 'On Primary' },
            { color: scheme.primaryContainer, label: 'Container' },
            { color: scheme.onPrimaryContainer, label: 'On Container' },
          ]}
        />
        <SwatchGroup
          title="Secondary"
          textColor={textColor}
          swatches={[
            { color: scheme.secondary, label: 'Secondary' },
            { color: scheme.onSecondary, label: 'On Sec.' },
            { color: scheme.secondaryContainer, label: 'Container' },
            { color: scheme.onSecondaryContainer, label: 'On Container' },
          ]}
        />
        <SwatchGroup
          title="Tertiary"
          textColor={textColor}
          swatches={[
            { color: scheme.tertiary, label: 'Tertiary' },
            { color: scheme.onTertiary, label: 'On Tert.' },
            { color: scheme.tertiaryContainer, label: 'Container' },
            { color: scheme.onTertiaryContainer, label: 'On Container' },
          ]}
        />
        <SwatchGroup
          title="Error"
          textColor={textColor}
          swatches={[
            { color: scheme.error, label: 'Error' },
            { color: scheme.onError, label: 'On Error' },
            { color: scheme.errorContainer, label: 'Container' },
            { color: scheme.onErrorContainer, label: 'On Container' },
          ]}
        />
        <SwatchGroup
          title="Surface"
          textColor={textColor}
          swatches={[
            { color: scheme.surface, label: 'Surface' },
            { color: scheme.onSurface, label: 'On Surface' },
            { color: scheme.surfaceVariant, label: 'Variant' },
            { color: scheme.onSurfaceVariant, label: 'On Variant' },
            { color: scheme.surfaceDim, label: 'Dim' },
            { color: scheme.surfaceBright, label: 'Bright' },
            { color: scheme.surfaceContainerLowest, label: 'Lowest' },
            { color: scheme.surfaceContainerLow, label: 'Low' },
            { color: scheme.surfaceContainer, label: 'Container' },
            { color: scheme.surfaceContainerHigh, label: 'High' },
            { color: scheme.surfaceContainerHighest, label: 'Highest' },
          ]}
        />
        <SwatchGroup
          title="Other"
          textColor={textColor}
          swatches={[
            { color: scheme.outline, label: 'Outline' },
            { color: scheme.outlineVariant, label: 'Out. Var.' },
            { color: scheme.inverseSurface, label: 'Inv. Surface' },
            { color: scheme.inverseOnSurface, label: 'Inv. On Surf.' },
            { color: scheme.inversePrimary, label: 'Inv. Primary' },
          ]}
        />
      </div>

      {/* Live UI Elements Preview */}
      <div className="p-4 space-y-4 border-t" style={{
        borderColor: `rgb(${cssVarsStyle['--color-outline-variant']})`,
        backgroundColor: `rgb(${cssVarsStyle['--color-surface']})`,
      }}>
        <h4 className="text-title-small font-bold" style={{ color: textColor }}>UI-Elemente Vorschau</h4>

        {/* Buttons */}
        <div className="flex flex-wrap gap-2">
          <button className="px-4 h-10 rounded-full text-label-large font-medium" style={{
            backgroundColor: scheme.primary, color: scheme.onPrimary,
          }}>Filled</button>
          <button className="px-4 h-10 rounded-full text-label-large font-medium" style={{
            backgroundColor: scheme.secondaryContainer, color: scheme.onSecondaryContainer,
          }}>Tonal</button>
          <button className="px-4 h-10 rounded-full text-label-large font-medium border" style={{
            backgroundColor: 'transparent', color: scheme.primary,
            borderColor: scheme.outline,
          }}>Outlined</button>
          <button className="px-4 h-10 rounded-full text-label-large font-medium" style={{
            backgroundColor: 'transparent', color: scheme.primary,
          }}>Text</button>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl p-3 shadow-elevation-1" style={{
            backgroundColor: scheme.surfaceContainerLow,
          }}>
            <span className="text-label-small font-bold" style={{ color: textColor }}>Elevated Card</span>
            <p className="text-[10px] mt-1" style={{ color: scheme.onSurfaceVariant }}>Beispiel-Inhalt</p>
          </div>
          <div className="rounded-xl p-3" style={{
            backgroundColor: scheme.surfaceContainerHighest,
          }}>
            <span className="text-label-small font-bold" style={{ color: textColor }}>Filled Card</span>
            <p className="text-[10px] mt-1" style={{ color: scheme.onSurfaceVariant }}>Beispiel-Inhalt</p>
          </div>
          <div className="rounded-xl p-3 border" style={{
            backgroundColor: scheme.surface,
            borderColor: scheme.outlineVariant,
          }}>
            <span className="text-label-small font-bold" style={{ color: textColor }}>Outlined Card</span>
            <p className="text-[10px] mt-1" style={{ color: scheme.onSurfaceVariant }}>Beispiel-Inhalt</p>
          </div>
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center h-8 px-3 rounded-sm border text-label-large" style={{
            borderColor: scheme.outline, color: textColor,
          }}>Chip</span>
          <span className="inline-flex items-center h-8 px-3 rounded-sm text-label-large" style={{
            backgroundColor: scheme.secondaryContainer, color: scheme.onSecondaryContainer,
          }}>Selected</span>
          <span className="inline-flex items-center h-8 px-3 rounded-sm text-label-large" style={{
            backgroundColor: scheme.tertiaryContainer, color: scheme.onTertiaryContainer,
          }}>Tertiary</span>
        </div>

        {/* FAB */}
        <div className="flex gap-3 items-center">
          <div className="w-14 h-14 rounded-lg flex items-center justify-center shadow-elevation-3" style={{
            backgroundColor: scheme.primaryContainer, color: scheme.onPrimaryContainer,
          }}>
            <span className="text-2xl">+</span>
          </div>
          <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-elevation-1" style={{
            backgroundColor: scheme.tertiary, color: scheme.onTertiary,
          }}>
            <span>★</span>
          </div>
          <div className="px-4 h-14 rounded-lg flex items-center justify-center shadow-elevation-3 gap-2" style={{
            backgroundColor: scheme.primaryContainer, color: scheme.onPrimaryContainer,
          }}>
            <span className="text-lg">+</span>
            <span className="text-label-large font-medium">Erstellen</span>
          </div>
        </div>

        {/* Text Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="h-14 rounded-xs border px-4 flex items-center" style={{
            borderColor: scheme.outline,
            backgroundColor: 'transparent',
          }}>
            <span className="text-body-large" style={{ color: scheme.onSurfaceVariant }}>Text eingeben...</span>
          </div>
          <div className="h-14 rounded-t-xs border-b-2 px-4 flex items-center" style={{
            borderColor: scheme.primary,
            backgroundColor: scheme.surfaceContainerHighest,
          }}>
            <span className="text-body-large" style={{ color: scheme.onSurfaceVariant }}>Filled Field</span>
          </div>
        </div>

        {/* Snackbar / Badge */}
        <div className="flex items-center gap-4">
          <div className="px-4 py-3 rounded-xs shadow-elevation-3 flex items-center gap-4" style={{
            backgroundColor: scheme.inverseSurface, color: scheme.inverseOnSurface,
          }}>
            <span className="text-body-medium">Snackbar-Nachricht</span>
            <span className="text-label-large font-medium" style={{ color: scheme.inversePrimary }}>Aktion</span>
          </div>
          <div className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-label-small font-medium" style={{
            backgroundColor: scheme.error, color: scheme.onError,
          }}>3</div>
        </div>

        {/* Navigation items */}
        <div className="flex gap-2">
          <div className="h-14 px-4 flex items-center gap-3 rounded-full" style={{
            backgroundColor: scheme.secondaryContainer, color: scheme.onSecondaryContainer,
          }}>
            <span>🏠</span>
            <span className="text-label-large font-medium">Aktiv</span>
          </div>
          <div className="h-14 px-4 flex items-center gap-3 rounded-full" style={{
            color: scheme.onSurfaceVariant,
          }}>
            <span>📋</span>
            <span className="text-label-large">Inaktiv</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorThemePreview;
